import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { fmtDate, fmtDateTime } from '../../lib/dates';
import { notifyStatusUpdate } from '../../lib/briefQuestionNotify';

// Outstanding supplier update requests + the owner's reply (respond-to-update
// brief). Besides Award and Close, the owner can send a HOLDING UPDATE — a
// revised expected decision date (+ optional message) that resolves every
// outstanding request on the brief and suppresses further chasing until the new
// date. Lives above Recipients & responses; only renders when a request is open.
//
// Audience defaults to EVERYONE STILL WAITING — the opposite of questions,
// because an update is a statement about the brief, not a reply to one supplier.

export default function BriefUpdateRequests({ briefId, briefName, currentUserId, briefLive = true, onChanged, onJumpToAward }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [contextSupplier, setContextSupplier] = useState(null); // { id, name } the composer was opened from
  const [decisionBy, setDecisionBy] = useState('');
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('all_waiting');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    // Load ALL requests (resolved + outstanding) so an answered one stays on the
    // page like supplier questions do, with the resolving update shown.
    const { data } = await supabase
      .from('brief_update_requests')
      .select('id, message, created_at, resolved_at, resolved_by_update_id, supplier_id, suppliers(supplier_name), requester:supplier_users!requested_by_user_id(full_name), resolution:brief_status_updates!resolved_by_update_id(decision_by, message, created_at, author:internal_users!author_user_id(full_name))')
      .eq('brief_id', briefId)
      .order('created_at', { ascending: false });
    setRequests(data || []);
    setLoading(false);
  }, [briefId]);
  useEffect(() => { load(); }, [load]);

  if (loading || requests.length === 0) return null;

  const outstandingCount = requests.filter((r) => !r.resolved_at).length;

  const openComposer = (req) => {
    setContextSupplier({ id: req.supplier_id, name: req.suppliers?.supplier_name || 'this supplier' });
    setAudience('all_waiting');
    setDecisionBy('');
    setMessage('');
    setComposerOpen(true);
  };

  const sendUpdate = async () => {
    if (!decisionBy) return;
    setSending(true);
    const { data: upd, error } = await supabase.from('brief_status_updates').insert([{
      brief_id: briefId,
      author_user_id: currentUserId || null,
      message: message.trim() || null,
      decision_by: decisionBy,
      audience,
      target_supplier_id: audience === 'asker' ? (contextSupplier?.id || null) : null,
    }]).select('id').single();
    if (error) { setSending(false); setNotice("Couldn't send the update — please try again."); return; }
    // Set the brief's expected decision date + resolve EVERY outstanding request
    // on the brief (not just the one viewed).
    // The update itself is recorded and about to be emailed, so neither of
    // these failing should stop it — but they must not fail silently either.
    // If the date doesn't land, suppliers are told to expect a decision by a
    // date the brief doesn't hold; if the requests don't resolve, they keep
    // showing as outstanding after being answered.
    const { error: dateErr } = await supabase.from('briefs').update({ decision_by: decisionBy }).eq('id', briefId);
    const { error: resolveErr } = await supabase.from('brief_update_requests')
      .update({ resolved_at: new Date().toISOString(), resolved_by_update_id: upd.id })
      .eq('brief_id', briefId).is('resolved_at', null);
    notifyStatusUpdate({
      briefId, briefName, decisionByLabel: fmtDate(decisionBy), message: message.trim(),
      audience, targetSupplierId: contextSupplier?.id, sentBy: currentUserId,
    }).catch(() => {});
    setSending(false);
    setComposerOpen(false);
    setNotice(dateErr || resolveErr
      ? `Update sent, but not everything saved: ${(dateErr || resolveErr).message}. Check the decision date on the brief.`
      : `Update sent — suppliers now expect a decision by ${fmtDate(decisionBy)}.`);
    load();
    onChanged?.();
  };

  return (
    <section id="brief-update-requests-section" aria-labelledby="brief-update-requests-heading" className="mt-8 scroll-mt-4">
      <div className="rounded-xl border border-line overflow-hidden" style={{ borderLeft: '4px solid #9A6208' }}>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-line bg-white">
          <h2 id="brief-update-requests-heading" className="font-['Metropolis'] text-sm font-bold text-ink">Update requests</h2>
          {outstandingCount > 0 ? (
            <span className="font-['Metropolis'] text-[11px] font-semibold text-[#7A4E10] bg-[#E89A2E]/15 border border-[#E89A2E]/40 rounded-md px-2 py-0.5">
              {outstandingCount} waiting
            </span>
          ) : (
            <span className="font-['Metropolis'] text-[11px] font-semibold text-fusion-forest bg-[#92D400]/10 border border-[#92D400]/40 rounded-md px-2 py-0.5">All handled</span>
          )}
        </div>

        <div className="p-5 bg-white space-y-4">
          {notice && <p role="status" className="font-['Metropolis'] text-[13px] text-fusion-forest">{notice}</p>}
          {outstandingCount > 0 && (
            <p className="font-['Metropolis'] text-[12px] text-[#51534A]">
              Resolve by awarding, closing, or sending a holding update with a new expected decision date. Any of the three clears these; answered requests stay here as a record.
            </p>
          )}

          {requests.map((req) => {
            const resolved = !!req.resolved_at;
            const res = req.resolution; // the holding update that resolved it, if any
            return (
              <div key={req.id} className={`border rounded-lg p-4 ${resolved ? 'border-line bg-[#FBFCF8]' : 'border-line'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-['Metropolis'] text-sm font-bold text-ink">{req.suppliers?.supplier_name || 'Supplier'}</p>
                    <p className="font-['Metropolis'] text-[11px] text-[#51534A]">{req.requester?.full_name ? `${req.requester.full_name} · ` : ''}asked {fmtDateTime(req.created_at)}</p>
                  </div>
                  <span className={`flex-shrink-0 font-['Metropolis'] text-[9px] font-bold uppercase tracking-[0.15em] rounded-md px-2 py-0.5 border whitespace-nowrap ${resolved ? 'bg-[#92D400]/10 text-fusion-forest border-[#92D400]/40' : 'bg-[#E89A2E]/15 text-[#7A4E10] border-[#E89A2E]/40'}`}>
                    {resolved ? 'Handled' : 'Waiting'}
                  </span>
                </div>
                {/* Hold-until message shown prominently — the operative fact. */}
                {req.message && req.message.trim() && (
                  <div className="mt-2 rounded-lg bg-[#FBF0DC] border border-[#EFD9A9] px-3 py-2">
                    <p className="font-['Metropolis'] text-sm text-[#5E3D05] whitespace-pre-wrap">{req.message.trim()}</p>
                  </div>
                )}

                {resolved ? (
                  <div className="mt-2 pt-2 border-t border-line">
                    {res ? (
                      <>
                        <div className="font-['Metropolis'] text-[11px] font-semibold text-fusion-forest mb-1">
                          {res.author?.full_name || 'Fusion 2K'} <span className="text-[#51534A] font-normal">· sent a holding update{res.decision_by ? ` · decision expected by ${fmtDate(res.decision_by)}` : ''}</span>
                        </div>
                        {res.message && res.message.trim() && <p className="font-['Metropolis'] text-sm text-ink whitespace-pre-wrap">{res.message.trim()}</p>}
                      </>
                    ) : (
                      <p className="font-['Metropolis'] text-[12px] text-[#51534A] italic">Resolved when the brief was awarded or closed.</p>
                    )}
                  </div>
                ) : briefLive ? (
                  /* Two visible routes (§2) — award/close and send-an-update. */
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <button type="button" onClick={() => openComposer(req)}
                      className="rounded-lg font-['Metropolis'] text-xs font-bold px-4 py-2 bg-[#92D400] text-[#131313] hover:bg-[#a4e810] transition-colors">
                      Send an update
                    </button>
                    <button type="button" onClick={onJumpToAward}
                      className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-[#51534A] hover:text-fusion-forest bg-transparent border border-line hover:border-[#DADCCD] rounded-full px-3 py-1.5 transition-colors">
                      Award or close instead
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}

          {/* Holding-update composer */}
          {composerOpen && (
            <div className="border border-[#92D400]/40 bg-[#F7F9F0] rounded-lg p-4">
              <p className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-fusion-forest mb-2">Send a holding update</p>
              <label htmlFor="update-decision-by" className="block font-['Metropolis'] text-[12px] font-semibold text-[#51534A] mb-1">Expected decision date <span className="text-[#E0290A]">*</span></label>
              <input
                id="update-decision-by" type="date" value={decisionBy}
                onChange={(e) => setDecisionBy(e.target.value)} disabled={sending}
                className="bg-white border border-line rounded-lg text-ink px-3 py-2 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] disabled:opacity-60" />
              <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1">A date alone is a useful answer — suppliers see it and won't chase again until it passes.</p>

              <label htmlFor="update-message" className="block font-['Metropolis'] text-[12px] font-semibold text-[#51534A] mb-1 mt-3">Message (optional) — suppliers see this word for word</label>
              <textarea
                id="update-message" rows={2} value={message}
                onChange={(e) => setMessage(e.target.value)} disabled={sending}
                placeholder="e.g. We're waiting on the client to confirm — should know by then."
                className="w-full bg-white border border-line rounded-lg text-ink px-3 py-2 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] disabled:opacity-60" />

              <fieldset className="mt-3">
                <legend className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.15em] text-[#51534A] mb-1">Who sees this update?</legend>
                <div className="flex flex-wrap gap-3">
                  <label className="flex items-center gap-2 cursor-pointer font-['Metropolis'] text-[13px] text-ink">
                    <input type="radio" name="update-audience" checked={audience === 'all_waiting'} onChange={() => setAudience('all_waiting')} className="accent-active-green" />
                    Everyone still waiting <span className="text-[#51534A]">(declined suppliers excluded)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-['Metropolis'] text-[13px] text-ink">
                    <input type="radio" name="update-audience" checked={audience === 'asker'} onChange={() => setAudience('asker')} className="accent-active-green" />
                    Just {contextSupplier?.name || 'the asker'}
                  </label>
                </div>
              </fieldset>

              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <button type="button" onClick={sendUpdate} disabled={sending || !decisionBy}
                  className="rounded-lg font-['Metropolis'] text-xs font-bold px-4 py-2 transition-colors bg-[#2F6E14] text-white hover:bg-[#3B6D11] disabled:bg-[#E6EADB] disabled:text-[#9BA08C] disabled:cursor-not-allowed">
                  {sending ? 'Sending…' : 'Send update'}
                </button>
                <button type="button" onClick={() => setComposerOpen(false)} disabled={sending}
                  className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border border-line rounded-full px-3 py-1.5 transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
